import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchTransportTypes } from '../data-provider';
import type { TransportType, TransportTypeFilters, TransportTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal transport type lookup cache used to resolve transport type references
 * without repeatedly requesting the same transport type data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const transportTypesQueryOptions = (
  filters: Partial<TransportTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const transportTypes = await fetchTransportTypes(filters, { signal });
      put(transportTypes.list);
      return transportTypes;
    },
    queryKey: CACHE_KEYS.transportTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_TRANSPORT_TYPES_CACHE_KEY = btoa('internal-transport-types-cache-key');

const INTERNAL_QUERY_TRANSPORT_TYPES_KEY = transportTypesQueryOptions({
  codeDesc: INTERNAL_TRANSPORT_TYPES_CACHE_KEY,
}).queryKey;

const getTransportTypeFallback = (transportTypeId: string): TransportType => {
  logger.warn(`Transport type "${transportTypeId}" was not found. Verify the data model.`);
  return {
    codeDesc: transportTypeId,
    id: transportTypeId,
    label: transportTypeId,
    lookupCodeType: 'LINERTYP',
  };
};

export const put = async (transportTypes: TransportType[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TRANSPORT_TYPES_KEY);

  const newTransportTypes = transportTypes.reduce(
    (acc, transportType) => {
      acc[String(transportType.id)] = transportType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_TRANSPORT_TYPES_KEY, {
    list: Object.values(newTransportTypes),
    map: newTransportTypes,
  });
};

export const getAll = (): TransportTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_TRANSPORT_TYPES_KEY);
};

export const find = async (transportTypeId: string): Promise<TransportType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TRANSPORT_TYPES_KEY);

  const transportType = data?.map[transportTypeId];
  if (transportType) {
    return transportType;
  }

  try {
    const newData = await queryClient.query(transportTypesQueryOptions({ search: { id: transportTypeId } }));
    if (newData.map[transportTypeId]) {
      void put(newData.list);
      return newData.map[transportTypeId];
    }
  } catch {
    return getTransportTypeFallback(transportTypeId);
  }

  return getTransportTypeFallback(transportTypeId);
};

export const findAll = async (transportTypeIds: string[]): Promise<TransportType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TRANSPORT_TYPES_KEY);

  const missingIds: string[] = [];
  const transportTypes: TransportType[] = [];

  for (const transportTypeId of transportTypeIds) {
    const transportType = data?.map[transportTypeId];
    if (transportType) {
      transportTypes.push(transportType);
    } else {
      missingIds.push(transportTypeId);
    }
  }

  if (!missingIds.length) {
    return transportTypes;
  }

  try {
    const newData = await queryClient.query(transportTypesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const transportType = newData.map[missingId];
      if (transportType) {
        transportTypes.push(transportType);
      } else {
        transportTypes.push(getTransportTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      transportTypes.push(getTransportTypeFallback(missingId));
    }
  }

  return transportTypes;
};
